import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Search,
  Users,
  GraduationCap,
  Eye,
  UserCheck,
  UserCog,
  ShieldCheck,
  Languages,
  Loader2,
  Shield,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth, useNavigation } from "../App";
import { ROLES } from "../constant/roles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { TabNavigation, TabItem } from "../components/ui/tab-navigation";
import { PageHeader } from "../components/ui/page-header";
import { UserProfileModal } from "../components/UserProfileModal";
import {
  useStaffUsers,
  useStudentUsers,
  useAdminUsers,
  useUserCounts,
  useUsers,
  User,
} from "../api/userManagementAPI";
import { useQuery } from "@tanstack/react-query";
import { getCoursesForProjectCreation } from "../api/gradesApi";
import { useEnhancedProjectsQuery } from "../api/projectManagementApi";

// Modern color palette for avatars
const AVATAR_COLORS = [
  { bg: "bg-bits-blue", text: "text-white" },
  { bg: "bg-bits-gold", text: "text-white" },
  { bg: "bg-bits-red", text: "text-white" },
  { bg: "bg-bits-light-blue", text: "text-white" },
  { bg: "bg-bits-gold", text: "text-white" },
  { bg: "bg-bits-red", text: "text-white" },
  { bg: "bg-bits-light-blue", text: "text-white" },
  { bg: "bg-bits-blue", text: "text-white" },
];

// Function to generate consistent color for a user based on their name
const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

interface UserRoleTabProps {
  role: string;
  title: string;
  userType: "staff" | "student" | "admin";
}

function UserRoleTab({ role, title, userType }: UserRoleTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [courseFilter, setCourseFilter] = useState<string | undefined>(
    undefined
  );
  const [projectFilter, setProjectFilter] = useState<string | undefined>(
    undefined
  );
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch courses for filter
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ["courses-project-creation"],
    queryFn: getCoursesForProjectCreation,
  });

  const courses = Array.isArray(coursesData)
    ? coursesData
    : coursesData?.data || [];

  // Fetch projects for filter - only when a course is selected
  const { data: projectsData, isLoading: projectsLoading } =
    useEnhancedProjectsQuery(
      {
        ...(courseFilter &&
          courseFilter !== "all-courses" && { courseId: courseFilter }),
        limit: 100, // Get more projects for filtering
      },
      {
        enabled: !!courseFilter && courseFilter !== "all-courses",
      }
    );

  const courseProjects = projectsData?.projects || [];

  // Fetch users using the new unified API with role-based filtering
  const {
    data: usersData,
    isLoading,
    error,
  } = useUsers({
    role: userType, // "student", "staff", or "admin"
    page: currentPage,
    limit: itemsPerPage,
    ...(statusFilter !== "all" && { status: statusFilter }),
    ...(searchTerm && { search: searchTerm }),
    ...(courseFilter &&
      courseFilter !== "all-courses" && { course_id: courseFilter }),
    ...(projectFilter &&
      projectFilter !== "all-projects" && { projectId: projectFilter }),
  });

  const users = usersData?.data?.users || [];
  const pagination = usersData?.data?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: itemsPerPage,
  };

  // Use server pagination data
  const totalPages = pagination.totalPages;

  // Use course projects for project filtering instead of extracting from user data
  const uniqueProjects =
    userType === "student" && courseProjects.length > 0 && courseFilter
      ? courseProjects.sort((a: any, b: any) => a.title?.localeCompare(b.title))
      : [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-bits-green-shade";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      case "suspended":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleIcon = (roleName: string) => {
    const lowerRole = roleName.toLowerCase();
    if (lowerRole === ROLES.STUDENT.toLowerCase()) {
      return <GraduationCap className="h-4 w-4 text-blue-600" />;
    } else if (lowerRole === ROLES.TA.toLowerCase()) {
      return <Shield className="h-4 w-4 text-bits-indigo" />;
    } else if (lowerRole === ROLES.INSTRUCTOR.toLowerCase()) {
      return <UserCheck className="h-4 w-4 text-blue-700" />;
    } else if (lowerRole === ROLES.ADMIN.toLowerCase()) {
      return <ShieldCheck className="h-4 w-4 text-red-600" />;
    }
    return null;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const getDisplayCourse = (user: User) => {
    if (courseFilter && user.courseRoles && user.courseRoles.length > 0) {
      const filteredCourse = user.courseRoles.find(
        (courseRole) => courseRole.course?.id === courseFilter
      );
      if (filteredCourse) {
        return filteredCourse.course.name;
      }
    }

    if (user.courseRoles && user.courseRoles.length > 0) {
      return user.courseRoles[0]?.course?.name || "-";
    }
    return "-";
  };

  const formatEmail = (email: string) => {
    if (!email || !email.includes("@")) {
      return "-";
    }
    return email;
  };

  // FIXED: Transform user data properly to include all fields for UserProfileModal
  const transformUserData = (user: User) => {
    const primaryRole = user.roles?.[0]?.name || "user";

    return {
      id: user.id,
      name: user.name,
      email: formatEmail(user.email),
      userId: user.lmsUserId || "-",
      studentId: user.lmsUserId || "-",
      role: primaryRole as any,
      status: user.status,
      enrollmentDate: user.courseRoles?.[0]?.enrolledAt || "",
      startDate: user.courseRoles?.[0]?.enrolledAt || "",
      lastLogin: user.lastLogin || "Never",
      department: user.courseRoles?.[0]?.course?.name || "",
      phone: user.phone || undefined,
      address: user.address || undefined,
      dateOfBirth: user.dateOfBirth || undefined,
      profilePicture: user.profilePicture || undefined,

      // Academic info for students
      currentGPA: user.gpa || undefined,
      gpa: user.gpa || undefined,
      totalCredits: user.totalCredits || undefined,
      academicYear: user.academicYear || undefined,
      year: user.academicYear || undefined,

      // Professional info for staff
      specialization: user.specialization || undefined,
      experience: user.experience || undefined,

      // Transform courses with proper structure including both naming conventions
      courses:
        user.courseRoles?.map((courseRole) => ({
          courseId: courseRole.course?.id || "",
          courseName: courseRole.course?.name || "",
          name: courseRole.course?.name || "",
          courseCode: courseRole.course?.code || "",
          code: courseRole.course?.code || "",
          instructor: courseRole.course?.instructor || "Not assigned",
          enrollmentDate: courseRole.enrolledAt || "",
          enrolledOn: courseRole.enrolledAt || "",
          status: "active" as any,
          role: (courseRole.role?.name || primaryRole) as any,
          currentGrade: courseRole.currentGrade || undefined,
          // Map projects if available
          projects:
            user.projects?.filter(
              (project: any) =>
                project.courseId === courseRole.course?.id ||
                project.course?.id === courseRole.course?.id
            ) || [],
        })) || [],

      // Include all projects for students
      projects: user.projects || [],

      // Pass the raw user ID for fetching detailed data
      rawUserId: user.id,
    };
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">{title}</h3>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${title.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 w-64"
              />
            </div>

            {userType !== ROLES.ADMIN && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select
                      value={courseFilter || "all-courses"}
                      onValueChange={(v) => {
                        setCourseFilter(v === "all-courses" ? undefined : v);
                        setProjectFilter(undefined); // Reset project filter when course changes
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[200px] cursor-pointer">
                        <SelectValue>
                          <span className="truncate block max-w-[160px]">
                            {courseFilter
                              ? courses.find((c: any) => c.id === courseFilter)
                                  ?.name || "Select course"
                              : "All courses"}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all-courses">All courses</SelectItem>
                        {coursesLoading ? (
                          <SelectItem value="loading" disabled>
                            Loading courses...
                          </SelectItem>
                        ) : (
                          courses.map((course: any) => (
                            <SelectItem key={course.id} value={course.id}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate block max-w-[160px]">
                                    {course.name} ({course.code})
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">
                                    {course.name} (Code: {course.code})
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="max-w-xs">
                    {courseFilter
                      ? courses.find((c: any) => c.id === courseFilter)?.name ||
                        "Select course"
                      : "All courses"}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}

            {userType === "student" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select
                      value={projectFilter || "all-projects"}
                      onValueChange={(v) => {
                        setProjectFilter(v === "all-projects" ? undefined : v);
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[200px] cursor-pointer">
                        <SelectValue>
                          <span className="truncate block max-w-[160px]">
                            {projectFilter
                              ? uniqueProjects.find(
                                  (p: any) => p.id === projectFilter
                                )?.title || "Select project"
                              : "All projects"}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all-projects">
                          All projects
                        </SelectItem>
                        {uniqueProjects.length === 0 ? (
                          <SelectItem value="no-projects" disabled>
                            No projects available
                          </SelectItem>
                        ) : (
                          uniqueProjects.map((project: any) => (
                            <SelectItem key={project.id} value={project.id}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate block max-w-[160px]">
                                    {project.title}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{project.title}</p>
                                </TooltipContent>
                              </Tooltip>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="max-w-xs">
                    {projectFilter
                      ? uniqueProjects.find((p: any) => p.id === projectFilter)
                          ?.title || "Select project"
                      : "All projects"}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => {
                      setStatusFilter(v);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[150px] cursor-pointer">
                      <SelectValue placeholder="Filter by Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>
                  {statusFilter === "all"
                    ? "All Status"
                    : statusFilter === "active"
                    ? "Active"
                    : "Inactive"}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-bits-blue" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-red-600">
                Error loading users: {error.message}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Data Table */}
        {!isLoading && !error && (
          <div className="border rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  {userType !== ROLES.ADMIN && <TableHead>Course</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user: User) => {
                  const avatarColor = getAvatarColor(user.name);
                  const primaryRole = user.roles?.[0]?.name || "user";

                  return (
                    <TableRow
                      key={user.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          {user.profilePicture &&
                          user.profilePicture.trim() !== "" &&
                          !imageErrors[user.id] ? (
                            <img
                              src={user.profilePicture}
                              alt={user.name}
                              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              onError={() => {
                                setImageErrors((prev) => ({
                                  ...prev,
                                  [user.id]: true,
                                }));
                              }}
                            />
                          ) : (
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                avatarColor?.bg || "bg-gray-500"
                              }`}
                            >
                              <span
                                className={`font-medium text-sm ${
                                  avatarColor?.text || "text-white"
                                }`}
                              >
                                {user.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2)}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-gray-900 truncate">
                                {user.name}
                              </p>
                            </div>
                            <p className="text-sm text-bits-grey-600 truncate">
                              {formatEmail(user.email)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {userType === "staff" ? (
                          <div className="flex gap-2">
                            {user.courseRoles && user.courseRoles.length > 0 ? (
                              Array.from(
                                new Set(
                                  user.courseRoles.map(
                                    (courseRole) => courseRole.roleInCourse
                                  )
                                )
                              ).map((uniqueRole, index) => (
                                <div
                                  key={index}
                                  className={`flex items-center space-x-1 px-2 py-1 rounded ${
                                    uniqueRole.toLowerCase() ===
                                    ROLES.INSTRUCTOR.toLowerCase()
                                      ? "bg-bits-blue-50"
                                      : uniqueRole.toLowerCase() ===
                                        ROLES.TA.toLowerCase()
                                      ? "bg-purple-50"
                                      : "bg-gray-50"
                                  }`}
                                >
                                  {getRoleIcon(uniqueRole)}
                                  <span className="text-sm font-medium text-blue-700 capitalize">
                                    {uniqueRole}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="flex items-center space-x-2">
                                {getRoleIcon(primaryRole)}
                                <span className="text-sm font-medium text-bits-grey-600 capitalize">
                                  {primaryRole}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            {getRoleIcon(primaryRole)}
                            <span className="text-sm font-medium text-bits-grey-600 capitalize">
                              {primaryRole}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      {userType !== ROLES.ADMIN && (
                        <TableCell>
                          <span className="text-sm text-bits-grey-600">
                            {getDisplayCourse(user)}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge className={getStatusColor(user.status)}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-bits-grey-600">
                          {formatDate(user.lastLogin)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <UserProfileModal
                          user={transformUserData(user)}
                          trigger={
                            <Button
                              size="sm"
                              variant="outline"
                              className="hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 transition-colors"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Profile
                            </Button>
                          }
                          onClose={() => setSelectedUser(null)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {!isLoading && !error && users.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                No {title} Found
              </h3>
              <p className="text-muted-foreground">
                Try adjusting your search criteria or filters.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pagination Section - Similar to Submissions Review */}
        {!isLoading && !error && users.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {pagination.currentPage} of {pagination.totalPages} •{" "}
              {pagination.totalItems} items
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoading}
                  className="h-9"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage(
                      Math.min(pagination.totalPages, currentPage + 1)
                    )
                  }
                  disabled={currentPage === pagination.totalPages || isLoading}
                  className="h-9"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(value) => {
                        setItemsPerPage(Number(value));
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[120px] h-9 cursor-pointer">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 page</SelectItem>
                        <SelectItem value="20">20 page</SelectItem>
                        <SelectItem value="50">50 page</SelectItem>
                        <SelectItem value="100">100 page</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{itemsPerPage} items per page</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

export default function UserManagementPage() {
  const { user } = useAuth();
  const { goBack } = useNavigation();
  const [activeTab, setActiveTab] = useState("students");

  const { data: userCountsData, isLoading: countsLoading } = useUserCounts();
  const { data: staffData } = useStaffUsers({ limit: 10, page: 1 });
  const { data: studentData } = useStudentUsers({ limit: 10, page: 1 });
  const { data: adminData } = useAdminUsers({ limit: 10, page: 1 });

  if (user?.role !== ROLES.ADMIN) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="space-y-4">
              <Users className="h-16 w-16 text-muted-foreground mx-auto" />
              <h2 className="text-2xl font-bold text-muted-foreground">
                Access Denied
              </h2>
              <p className="text-muted-foreground">
                You don't have permission to access user management. This
                feature is restricted to administrators only.
              </p>
              <Button
                onClick={goBack}
                className="bg-bits-blue hover:bg-bits-blue/90"
              >
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const userCounts = userCountsData?.data;

  const totalUsers =
    userCounts?.total_users ||
    (staffData?.data?.pagination?.totalItems || 0) +
      (studentData?.data?.pagination?.totalItems || 0) +
      (adminData?.data?.pagination?.totalItems || 0);

  const totalStudents =
    userCounts?.students || studentData?.data?.pagination?.totalItems || 0;

  const totalStaff = userCounts?.staff
    ? userCounts.staff.instructors + userCounts.staff.teaching_assistants
    : staffData?.data?.pagination?.totalItems || 0;

  const totalAdmins = adminData?.data?.pagination?.totalItems || 0;
  const activeUsers = userCounts?.active_users || totalUsers;
  const inactiveUsers = userCounts?.inactive_users || 0;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="User Management"
        subtitle="View and manage all platform users"
      />

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card className="justify-center" style={{ height: 100 }}>
          <CardHeader className="flex items-center justify-between pt-0">
            <CardTitle className="text-base text-bits-grey-600">
              Total Users
              <div className="text-3xl font-bold text-black">
                {countsLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  totalUsers
                )}
              </div>
            </CardTitle>
            <Users className="text-bits-blue" />
          </CardHeader>
        </Card>
        <Card className="justify-center" style={{ height: 100 }}>
          <CardHeader className="flex items-center justify-between pt-0">
            <CardTitle className="text-base text-bits-grey-600">
              Students
              <div className="text-3xl font-bold text-black">
                {countsLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  totalStudents
                )}
              </div>
            </CardTitle>
            <GraduationCap className="text-bits-blue" />
          </CardHeader>
        </Card>
        <Card className="justify-center" style={{ height: 100 }}>
          <CardHeader className="flex items-center justify-between pt-0">
            <CardTitle className="text-base text-bits-grey-600">
              Staff
              <div className="text-3xl font-bold text-black">
                {countsLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  totalStaff
                )}
              </div>
            </CardTitle>
            <Languages className="text-bits-blue" />
          </CardHeader>
        </Card>
        <Card className="justify-center" style={{ height: 100 }}>
          <CardHeader className="flex items-center justify-between pt-0">
            <CardTitle className="text-base text-bits-grey-600">
              Active Users
              <div className="text-3xl font-bold text-black">
                {countsLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  activeUsers
                )}
              </div>
            </CardTitle>
            <UserCheck className="text-bits-blue" />
          </CardHeader>
        </Card>
        <Card className="justify-center" style={{ height: 100 }}>
          <CardHeader className="flex items-center justify-between pt-0">
            <CardTitle className="text-base text-bits-grey-600">
              Inactive Users
              <div className="text-3xl font-bold text-black">
                {countsLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  inactiveUsers
                )}
              </div>
            </CardTitle>
            <UserCheck className="text-bits-blue" />
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Platform Users</CardTitle>
          <CardDescription>
            Browse and manage users by role category
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TabNavigation
            tabs={[
              {
                key: "students",
                label: "Students",
                icon: <GraduationCap className="h-4 w-4" />,
                count: totalStudents,
              },
              {
                key: "staff",
                label: "Staff",
                icon: <UserCog className="h-4 w-4" />,
                count: totalStaff,
              },
              {
                key: "admins",
                label: "Admins",
                icon: <ShieldCheck className="h-4 w-4" />,
                count: totalAdmins,
              },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="mb-6"
          />

          {activeTab === "students" && (
            <UserRoleTab
              role={ROLES.STUDENT}
              title="Students"
              userType="student"
            />
          )}

          {activeTab === "staff" && (
            <UserRoleTab
              role={ROLES.INSTRUCTOR}
              title="Staff"
              userType="staff"
            />
          )}

          {activeTab === "admins" && (
            <UserRoleTab
              role={ROLES.ADMIN}
              title="Administrators"
              userType="admin"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
